import React, { useState, useCallback, useRef, useEffect } from "react";
import {
  Search,
  X,
  Eye,
  Download,
  ChevronDown,
  BookOpen,
  Loader,
  AlertCircle,
  HelpCircle,
  ArrowLeft,
  ImageIcon,
} from "lucide-react";
import { useAuth } from "../context/ApiKeyContext";
import { markdownToHtml } from "../utils/markdownConverter";
import OptimizedPDFViewer from "./OptimizedPDFViewer";
import { getApiBaseUrl } from "../config";

const API_BASE_URL = getApiBaseUrl();

/**
 * Componente auxiliar para exibir uma imagem de tema carregada via token da API
 */
const TemaImagePreview = ({ imageToken, legenda, authToken }) => {
  const [imageUrl, setImageUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!imageToken || !authToken) return;

    let cancelled = false;

    const fetchImage = async () => {
      try {
        setLoading(true);
        setError(null);

        const downloadUrl = `${API_BASE_URL}/files/download?token=${imageToken}&Servicename=${encodeURIComponent("DOCUMENT_CREATION")}`;

        const response = await fetch(downloadUrl, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${authToken}`,
            accept: "application/octet-stream",
          },
        });

        if (!response.ok) {
          throw new Error(`Erro ao carregar imagem: ${response.status}`);
        }

        const blob = await response.blob();
        if (!cancelled) {
          const url = window.URL.createObjectURL(blob);
          setImageUrl(url);
        }
      } catch (err) {
        if (!cancelled) {
          console.error("❌ Erro ao carregar imagem do tema:", err);
          setError(err.message);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchImage();

    return () => {
      cancelled = true;
      setImageUrl((prevUrl) => {
        if (prevUrl) {
          window.URL.revokeObjectURL(prevUrl);
        }
        return null;
      });
    };
  }, [imageToken, authToken]);

  if (loading) {
    return (
      <div className="tema-image-loading">
        <Loader size={16} className="spin-animation" />
        <span className="text-sm">Carregando imagem...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="tema-image-error">
        <ImageIcon size={16} />
        <span>Não foi possível carregar a imagem</span>
      </div>
    );
  }

  if (!imageUrl) return null;

  return (
    <div className="tema-image-wrapper">
      <img
        src={imageUrl}
        alt={legenda || "Imagem do tema"}
        className="tema-image"
      />
      {legenda && <p className="tema-image-caption">{legenda}</p>}
    </div>
  );
};

/**
 * Modal de pesquisa de temas para importação em documentos
 * Permite buscar temas por palavras-chave e/ou contexto semântico
 */
const TemaSearchModal = ({
  isOpen,
  onClose,
  onImportTema,
  cellId,
  position, // 'above' ou 'below'
  referenceItemId, // ID do item de referência para inserção
}) => {
  const { token } = useAuth();

  // Estados do formulário de pesquisa
  const [palavrasChave, setPalavrasChave] = useState("");
  const [contexto, setContexto] = useState("");
  const [pesquisarDocumentoCompleto, setPesquisarDocumentoCompleto] =
    useState(false);

  // Estados de resultados e carregamento
  const [resultados, setResultados] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searched, setSearched] = useState(false);

  // Estados de expansão dos cards
  const [expandedAssuntos, setExpandedAssuntos] = useState({});
  const [expandedTemas, setExpandedTemas] = useState({});

  // Estado de importação
  const [importingTemaId, setImportingTemaId] = useState(null);

  // Estado para visualização de PDF
  const [viewingPdf, setViewingPdf] = useState(null); // { token, filename, titulo }

  // Ref para controlar se o mousedown foi no overlay (para evitar fechar ao soltar mouse após selecionar texto)
  const mouseDownOnOverlay = useRef(false);

  // Função para realizar a pesquisa
  const handleSearch = useCallback(async () => {
    // Validar que pelo menos um campo de pesquisa foi preenchido
    if (!palavrasChave.trim() && !contexto.trim()) {
      setError("Informe pelo menos palavras-chave ou contexto para pesquisar.");
      return;
    }

    setLoading(true);
    setError(null);
    setSearched(true);

    try {
      const url = `${API_BASE_URL}/doc_creator/teses/pesquisar?cell_id=${cellId}`;

      const body = {};
      if (palavrasChave.trim()) {
        body.palavras_chave = palavrasChave.trim();
      }
      if (contexto.trim()) {
        body.contexto = contexto.trim();
      }
      body.pesquisar_documento_completo = pesquisarDocumentoCompleto;

      console.log("🔍 Pesquisando temas:", { url, body });

      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          accept: "application/json",
        },
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        let errorMsg = "Erro ao pesquisar temas";

        // Tentar obter mensagem de erro da API primeiro
        try {
          const errorData = await response.json();
          errorMsg = errorData?.detail || errorData?.message || errorMsg;
        } catch {
          // Se não conseguir parsear JSON, usar mensagens padrão por código HTTP
          if (response.status === 400) {
            errorMsg = "Nenhum parâmetro de pesquisa fornecido.";
          } else if (response.status === 401) {
            errorMsg = "Não autorizado. Verifique suas credenciais.";
          } else if (response.status === 403) {
            errorMsg = "Permissão negada a este recurso.";
          } else if (response.status === 500) {
            errorMsg = "Erro interno do servidor. Tente novamente mais tarde.";
          } else {
            errorMsg = `Erro ${response.status}: ${response.statusText}`;
          }
        }

        throw new Error(errorMsg);
      }

      const data = await response.json();
      console.log("✅ Resultados da pesquisa:", data);
      setResultados(data || []);
    } catch (err) {
      console.error("❌ Erro na pesquisa de temas:", err);
      setError(err.message || "Erro ao pesquisar temas");
      setResultados([]);
    } finally {
      setLoading(false);
    }
  }, [palavrasChave, contexto, pesquisarDocumentoCompleto, cellId, token]);

  // Função para importar um tema
  const handleImportTema = useCallback(
    async (tema) => {
      setImportingTemaId(tema.id_chunk_tema);

      try {
        // Chamar o callback de importação passando o id_chunk_tema
        await onImportTema({
          id_chunk_tema: tema.id_chunk_tema,
          position,
          referenceItemId,
        });

        // Fechar o modal após importação bem-sucedida
        onClose();
      } catch (err) {
        console.error("❌ Erro ao importar tema:", err);
        setError("Erro ao importar tema. Tente novamente.");
      } finally {
        setImportingTemaId(null);
      }
    },
    [onImportTema, onClose, position, referenceItemId],
  );

  // Função para visualizar PDF
  const handleViewPdf = useCallback((tema) => {
    // Verificar se o tema tem dados do arquivo para visualização
    if (tema.file && tema.file.token) {
      console.log("📄 Abrindo visualização de PDF:", tema.file);
      setViewingPdf({
        token: tema.file.token,
        filename: tema.file.filename || "documento.pdf",
        titulo: tema.titulo_tema,
        processo: tema.numero_processo,
        pagina: tema.file.pagina || 1,
      });
    } else {
      console.warn("⚠️ Tema não possui arquivo para visualização:", tema);
      setError(
        "Este tema não possui arquivo PDF disponível para visualização.",
      );
    }
  }, []);

  // Função para fechar a visualização de PDF
  const handleClosePdfViewer = useCallback(() => {
    setViewingPdf(null);
  }, []);

  // Toggle para expandir/colapsar assunto
  const toggleAssunto = useCallback((id) => {
    setExpandedAssuntos((prev) => ({ ...prev, [id]: !prev[id] }));
  }, []);

  // Toggle para expandir/colapsar tema completo
  const toggleTema = useCallback((id) => {
    setExpandedTemas((prev) => ({ ...prev, [id]: !prev[id] }));
  }, []);

  // Resetar estados ao fechar
  const handleClose = useCallback(() => {
    setPalavrasChave("");
    setContexto("");
    setPesquisarDocumentoCompleto(false);
    setResultados([]);
    setError(null);
    setSearched(false);
    setExpandedAssuntos({});
    setExpandedTemas({});
    onClose();
  }, [onClose]);

  if (!isOpen) return null;

  return (
    <div
      className="tema-modal-overlay"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) {
          mouseDownOnOverlay.current = true;
        }
      }}
      onMouseUp={(e) => {
        if (e.target === e.currentTarget && mouseDownOnOverlay.current) {
          if (viewingPdf) {
            handleClosePdfViewer();
          } else {
            handleClose();
          }
        }
        mouseDownOnOverlay.current = false;
      }}
    >
      {/* Visualizador de PDF */}
      {viewingPdf && (
        <div
          className="tema-pdf-viewer"
          onMouseDown={() => {
            mouseDownOnOverlay.current = false;
          }}
        >
          {/* Header do PDF Viewer */}
          <div className="tema-pdf-header">
            <button className="tema-back-btn" onClick={handleClosePdfViewer}>
              <ArrowLeft size={16} />
              Voltar
            </button>
            <div className="flex-1 min-w-0">
              <h3 className="tema-pdf-title">
                {viewingPdf.titulo || "Visualização do PDF"}
              </h3>
              {viewingPdf.processo && (
                <span className="tema-mono-text">
                  Processo: {viewingPdf.processo}
                </span>
              )}
            </div>
            <button
              className="tema-close-btn"
              onClick={handleClosePdfViewer}
              title="Fechar"
            >
              <X size={20} />
            </button>
          </div>

          {/* Área do PDF */}
          <div className="tema-pdf-content">
            <div className="flex-1 overflow-hidden">
              <OptimizedPDFViewer
                token={viewingPdf.token}
                fileName={viewingPdf.filename}
                serviceName="DOCUMENT_CREATION"
                initialPageNumber={viewingPdf.pagina}
                showToolbar={true}
                width="100%"
                height="100%"
                editavel={false}
              />
            </div>
          </div>
        </div>
      )}

      {/* Modal de Pesquisa (escondido quando visualizando PDF) */}
      <div
        className="tema-modal"
        onMouseDown={() => {
          mouseDownOnOverlay.current = false;
        }}
        style={viewingPdf ? { display: "none" } : undefined}
      >
        {/* Header */}
        <div className="tema-search-header">
          <h2 className="tema-search-title">
            <span className="tema-search-icon">
              <Search size={20} />
            </span>
            Pesquisa de Temas
          </h2>
          <button
            className="tema-close-btn"
            onClick={handleClose}
            title="Fechar"
          >
            <X size={20} />
          </button>
        </div>

        {/* Área de Pesquisa */}
        <div className="tema-search-area">
          <div className="tema-search-grid">
            <div className="flex-col gap-6">
              <label className="tema-field-label">Palavras-chave</label>
              <input
                type="text"
                className="tema-search-input"
                value={palavrasChave}
                onChange={(e) => setPalavrasChave(e.target.value)}
                placeholder="Digite as palavras-chave..."
                onKeyPress={(e) => e.key === "Enter" && handleSearch()}
              />
            </div>
            <div className="flex-col gap-6">
              <label className="tema-field-label">Contexto</label>
              <input
                type="text"
                className="tema-search-input"
                value={contexto}
                onChange={(e) => setContexto(e.target.value)}
                placeholder="Informe o contexto da pesquisa..."
                onKeyPress={(e) => e.key === "Enter" && handleSearch()}
              />
            </div>
          </div>

          <div className="flex-row-between gap-16 flex-wrap">
            <label className="tema-checkbox-label">
              <input
                type="checkbox"
                className="tema-checkbox"
                checked={pesquisarDocumentoCompleto}
                onChange={(e) =>
                  setPesquisarDocumentoCompleto(e.target.checked)
                }
              />
              <span>
                Pesquisar em toda a contestação (não apenas nos temas)
              </span>
              <span
                className="tema-help-icon"
                title="Quando ativado, a pesquisa será feita em todo o conteúdo da contestação, não apenas nos temas extraídos. Isso retornará todos os temas de documentos que atendem aos filtros de palavras-chave e contexto informados."
              >
                <HelpCircle size={16} />
              </span>
            </label>

            <button
              className="tema-search-btn"
              onClick={handleSearch}
              disabled={loading}
            >
              {loading ? (
                <Loader size={18} className="spin-animation" />
              ) : (
                <Search size={18} />
              )}
              {loading ? "Pesquisando..." : "Pesquisar"}
            </button>
          </div>
        </div>

        {/* Área de Resultados */}
        <div className="tema-results-area">
          {/* Mensagem de erro */}
          {error && (
            <div className="tema-error-message">
              <AlertCircle size={20} />
              <span>{error}</span>
            </div>
          )}

          {/* Cabeçalho de resultados */}
          {searched && !loading && (
            <div className="tema-results-summary">
              <div className="flex-row-between">
                <span className="tema-results-count">
                  <strong>{resultados.length}</strong>{" "}
                  {resultados.length === 1
                    ? "resultado encontrado"
                    : "resultados encontrados"}
                </span>
              </div>
              {resultados.length === 10 && (
                <div className="tema-results-warning">
                  <AlertCircle size={16} className="flex-shrink-0" />
                  <span>
                    Os resultados estão limitados a no máximo 10 itens. Refine
                    sua pesquisa para encontrar resultados mais específicos.
                  </span>
                </div>
              )}
            </div>
          )}

          {/* Estado de carregamento */}
          {loading && (
            <div className="tema-state-container">
              <Loader
                size={48}
                className="tema-loading-spinner spin-animation"
              />
              <p className="tema-state-title">Pesquisando temas...</p>
              <p className="tema-state-text mt-8">
                Aguarde enquanto buscamos os resultados
              </p>
            </div>
          )}

          {/* Estado vazio (antes da pesquisa) */}
          {!searched && !loading && (
            <div className="tema-state-container">
              <div className="tema-empty-icon">
                <Search size={40} />
              </div>
              <p className="tema-state-title">Pesquise por temas</p>
              <p className="tema-state-text-narrow">
                Utilize os campos acima para buscar temas por palavras-chave ou
                contexto semântico.
              </p>
            </div>
          )}

          {/* Estado vazio (sem resultados) */}
          {searched && !loading && resultados.length === 0 && !error && (
            <div className="tema-state-container">
              <div className="tema-empty-icon">
                <BookOpen size={40} />
              </div>
              <p className="tema-state-title">Nenhum tema encontrado</p>
              <p className="tema-state-text-narrow">
                Tente ajustar os termos de pesquisa ou ativar a opção de
                pesquisar em toda a contestação.
              </p>
            </div>
          )}

          {/* Lista de resultados */}
          {!loading &&
            resultados.map((tema) => (
              <div key={tema.id_chunk_tema} className="tema-result-card">
                {/* Cabeçalho do card */}
                <div className="tema-card-header">
                  <span className="tema-card-title">
                    {tema.titulo_tema || "Tema sem título"}
                  </span>
                  <div className="flex-row gap-8 flex-shrink-0">
                    <button
                      className="tema-view-btn"
                      onClick={() => handleViewPdf(tema)}
                      disabled={!tema.file || !tema.file.token}
                      title={
                        tema.file && tema.file.token
                          ? "Visualizar PDF"
                          : "PDF não disponível"
                      }
                    >
                      <Eye size={18} />
                    </button>
                    <button
                      className="tema-import-btn"
                      onClick={() => handleImportTema(tema)}
                      disabled={importingTemaId === tema.id_chunk_tema}
                    >
                      {importingTemaId === tema.id_chunk_tema ? (
                        <Loader size={16} className="spin-animation" />
                      ) : (
                        <Download size={16} />
                      )}
                      {importingTemaId === tema.id_chunk_tema
                        ? "Importando..."
                        : "Importar Tema"}
                    </button>
                  </div>
                </div>

                {/* Corpo do card */}
                <div className="tema-card-body">
                  {/* Processo */}
                  <div className="tema-info-row mb-12">
                    <span className="tema-info-label">Processo:</span>
                    <span className="tema-mono-text">
                      {tema.numero_processo || "N/A"}
                    </span>
                  </div>

                  {/* Tipo da Ação (expansível) */}
                  <div className="tema-info-row items-start mb-12">
                    <span
                      className="tema-info-label"
                      style={{ paddingTop: "10px" }}
                    >
                      Tipo da Ação:
                    </span>
                    <div className="flex-1">
                      <div
                        className="tema-expand-trigger"
                        onClick={() => toggleAssunto(tema.id_chunk_tema)}
                        style={{
                          borderRadius: expandedAssuntos[tema.id_chunk_tema]
                            ? "8px 8px 0 0"
                            : "8px",
                        }}
                      >
                        <span className="tema-expand-trigger-text">
                          {tema.tipo_acao || "Não informado"}
                        </span>
                        <ChevronDown
                          size={16}
                          className="tema-chevron"
                          style={{
                            transform: expandedAssuntos[tema.id_chunk_tema]
                              ? "rotate(180deg)"
                              : "rotate(0deg)",
                          }}
                        />
                      </div>
                      {expandedAssuntos[tema.id_chunk_tema] &&
                        tema.resumo_inicial_completo && (
                          <div className="tema-expand-content">
                            <div className="tema-expand-sublabel">
                              Resumo da Inicial
                            </div>
                            <div
                              style={{ textAlign: "justify" }}
                              dangerouslySetInnerHTML={{
                                __html: markdownToHtml(
                                  tema.resumo_inicial_completo || "",
                                ),
                              }}
                            />
                          </div>
                        )}
                    </div>
                  </div>

                  {/* Tema Completo (expansível) */}
                  <div className="tema-info-row items-start">
                    <span
                      className="tema-info-label"
                      style={{ paddingTop: "10px" }}
                    >
                      Tema Completo:
                    </span>
                    <div className="tema-tema-wrapper">
                      <div
                        className="tema-tema-toggle"
                        onClick={() => toggleTema(tema.id_chunk_tema)}
                      >
                        <span className="tema-tema-toggle-text">
                          <BookOpen size={14} />
                          {expandedTemas[tema.id_chunk_tema]
                            ? "Clique para recolher"
                            : "Clique para expandir"}
                        </span>
                        <ChevronDown
                          size={16}
                          className="tema-chevron"
                          style={{
                            transform: expandedTemas[tema.id_chunk_tema]
                              ? "rotate(180deg)"
                              : "rotate(0deg)",
                          }}
                        />
                      </div>
                      {expandedTemas[tema.id_chunk_tema] && (
                        <div className="tema-tema-content">
                          {/* Renderizar conteúdo misto: texto (MD) e imagens intercalados */}
                          {tema.conteudo_itens &&
                          Array.isArray(tema.conteudo_itens) &&
                          tema.conteudo_itens.length > 0 ? (
                            // Se a API retornar blocos de conteúdo ordenados (texto e imagem intercalados)
                            tema.conteudo_itens.map((bloco, idx) => {
                              if (bloco.tipo === "IMAGEM" && bloco.token) {
                                return (
                                  <TemaImagePreview
                                    key={`img-${idx}`}
                                    imageToken={bloco.token}
                                    legenda={
                                      bloco.legenda ||
                                      bloco.texto_de_interesse ||
                                      ""
                                    }
                                    authToken={token}
                                  />
                                );
                              }
                              // TEXTO ou fallback
                              const textoBloco =
                                bloco.texto || bloco.conteudo || "";
                              if (!textoBloco) return null;
                              return (
                                <div
                                  key={`txt-${idx}`}
                                  dangerouslySetInnerHTML={{
                                    __html: markdownToHtml(textoBloco),
                                  }}
                                />
                              );
                            })
                          ) : (
                            // Fallback: renderizar conteudo_tema como MD + imagens separadas
                            <>
                              {tema.conteudo_tema && (
                                <div
                                  dangerouslySetInnerHTML={{
                                    __html: markdownToHtml(tema.conteudo_tema),
                                  }}
                                />
                              )}
                              {/* Renderizar imagens do tema se existirem */}
                              {tema.imagens_tema &&
                                Array.isArray(tema.imagens_tema) &&
                                tema.imagens_tema.length > 0 && (
                                  <div style={{ marginTop: "12px" }}>
                                    {tema.imagens_tema.map((img, idx) => (
                                      <TemaImagePreview
                                        key={`tema-img-${idx}`}
                                        imageToken={img.token}
                                        legenda={
                                          img.legenda ||
                                          img.texto_de_interesse ||
                                          ""
                                        }
                                        authToken={token}
                                      />
                                    ))}
                                  </div>
                                )}
                              {/* Caso não tenha nem conteudo_tema nem imagens */}
                              {!tema.conteudo_tema &&
                                (!tema.imagens_tema ||
                                  tema.imagens_tema.length === 0) && (
                                  <span className="italic text-muted">
                                    Conteúdo não disponível
                                  </span>
                                )}
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
        </div>
      </div>
    </div>
  );
};

export default TemaSearchModal;
